/**
 * The status vocabulary, as the browser renders it.
 *
 * ---------------------------------------------------------------------------
 * THIS FILE IS WHY THE COLLECT LAYER EXISTS
 * ---------------------------------------------------------------------------
 * Every honest status the backend can produce has to survive contact with a
 * screen. Three rules, enforced here so no component can break them
 * independently:
 *
 *  1. **Every status has a WORD.** `labelFor` never returns an empty string, so
 *     colour is never the only carrier of state (WCAG 1.4.1, and also common
 *     sense on a phone in sunlight). A `below_request_floor` renders as
 *     "below request floor", not as a grey dash.
 *  2. **No status renders as a number it does not have.** `valueText` returns
 *     the word for anything that is not `ok`; there is no `?? 0`, no `|| '—'`
 *     that could be mistaken for a measurement, and no branch where an
 *     unmeasured thing acquires a digit.
 *  3. **`ok` is not the fallback.** The mapping is exhaustive over the union and
 *     the default arm returns `unknown`, tone `warn`. A new backend status
 *     therefore shows up as something odd on screen rather than as a green tick.
 *
 * It is deliberately pure and DOM-free so `test/ui/**` can assert on it directly
 * — the rules above are testable claims, not styling opinions.
 */

export type SignalStatus = 'ok' | 'empty' | 'unproven' | 'blocked' | 'error';

export type PercentileStatus =
  | 'ok'
  | 'above_top_bucket'
  | 'below_request_floor'
  | 'empty'
  | 'unproven'
  | 'blocked'
  | 'error';

export type AnyStatus = SignalStatus | PercentileStatus | 'unknown';

export interface Signal<T> {
  status: string;
  value?: T;
  reason?: string;
  detail?: string;
  gate?: string;
  kind?: string;
  message?: string;
  [extra: string]: unknown;
}

/**
 * Visual tone. `neutral` is used for honest non-measurements so they do not
 * masquerade as failures either — an `empty` is not an incident.
 */
export type Tone = 'good' | 'bad' | 'warn' | 'neutral' | 'blocked';

/** The word. Never empty, never a symbol on its own. */
export function labelFor(status: string): string {
  switch (status) {
    case 'ok':
      return 'ok';
    case 'empty':
      return 'empty';
    case 'unproven':
      return 'unproven';
    case 'blocked':
      return 'blocked';
    case 'error':
      return 'error';
    case 'above_top_bucket':
      return 'above top bucket';
    case 'below_request_floor':
      return 'below request floor';
    case 'breached':
      return 'breached';
    case 'GREEN':
      return 'GREEN';
    case 'AMBER':
      return 'AMBER';
    case 'RED':
      return 'RED';
    // Cluster-level outcomes (`MaxScaleOutcome`, `PsmdbOutcome`, and the CNPG
    // outcome this API derives). They travel on `outcome`, not `status`, but they
    // go through this same table — so they belong in it.
    case 'healthy':
      return 'healthy';
    case 'degraded':
      return 'degraded';
    case 'unknown':
    case 'unknown-role':
      return 'unknown';
    default:
      return status;
  }
}

export function toneFor(status: string): Tone {
  switch (status) {
    case 'ok':
    case 'GREEN':
    case 'healthy':
      return 'good';
    case 'error':
    case 'RED':
    case 'breached':
    case 'above_top_bucket':
    case 'degraded':
      return 'bad';
    case 'unproven':
    case 'AMBER':
    case 'unknown':
    case 'unknown-role':
      return 'warn';
    case 'blocked':
      return 'blocked';
    case 'empty':
    case 'below_request_floor':
      return 'neutral';
    default:
      // An unrecognised status is not good news. It is a thing to look at.
      return 'warn';
  }
}

/* ------------------------------------------------------------------ *
 * THE EPISTEMIC AXIS — "do we know?", independent of "is it bad?"
 * ------------------------------------------------------------------ */

export type Epistemic = 'measured' | 'unmeasured' | 'unmeasurable';

/**
 * The statuses that are CLAIMS ABOUT A MEASUREMENT, and therefore the only ones
 * that may wear an OUTLINED container.
 *
 * This set is load-bearing in the opposite direction from everything else in
 * this file. `GREEN`/`AMBER`/`RED` are an agent's JUDGEMENT, `healthy`/
 * `degraded` is a CR's own outcome, `new`/`known`/`resolved` is a ledger
 * workflow state. None of them is a measurement, and outlining them would dilute
 * the container channel until it meant nothing — which is precisely the failure
 * the channel exists to prevent. An AMBER verdict is a firm conclusion, not an
 * absence of data, and it must look like one.
 *
 * `unknown` and `unknown-role` ARE in the set: a cluster CR reporting no
 * primary, or a MaxScale outcome of `unknown`, is exactly "we do not know", and
 * it earns the dotted edge.
 */
const MEASUREMENT_STATUSES = new Set([
  'ok',
  'empty',
  'unproven',
  'blocked',
  'error',
  'above_top_bucket',
  'below_request_floor',
  'unknown',
  'unknown-role',
]);

export function isMeasurementStatus(status: string): boolean {
  return MEASUREMENT_STATUSES.has(status);
}

/**
 * How much this status knows about the world.
 *
 * This is the SECOND channel of the design system, and it is the whole reason
 * the dashboard can carry a four-way vocabulary honestly. `toneFor` answers
 * "is it bad?" with hue; this answers "did we actually look?" with the CONTAINER
 * (`styles.css`: `.epi-measured`, `.epi-unmeasured`, `.epi-unmeasurable` —
 * filled, dotted, dashed).
 *
 * The two axes are genuinely independent, and every combination occurs:
 *
 *   measured + good        `ok`                     FILLED, ink        tick
 *   measured + bad         `above_top_bucket`       FILLED, red        chevrons
 *   measured + neutral     `empty`                  FILLED, ink        minus
 *   unmeasured + warn      `unproven`               DOTTED, ink        dashed ring
 *   unmeasured + bad       `error`                  DOTTED, red        cross
 *   unmeasurable + blocked `blocked`                DASHED, ink        strike
 *
 * The critical pair is `empty` vs `unproven`. Both mean "no number". Hue alone
 * cannot separate them at a glance — but a FILLED chip versus a DOTTED one can,
 * and does, before the words are read. That is the distinction the collect layer
 * spent a tranche protecting, and this is where it survives contact with a
 * screen.
 *
 * An UNRECOGNISED status is `unmeasured`, never `measured`: a status this build
 * has never heard of tells us nothing about the world, and the honest rendering
 * of "we do not know what this means" is the same dotted edge as "we did not
 * look".
 * Callers that render a non-measurement vocabulary guard with
 * {@link isMeasurementStatus} first and leave the container filled.
 */
export function epistemicFor(status: string): Epistemic {
  switch (status) {
    // We looked. These four are all real measurement OUTCOMES, including the
    // two percentile edges — `below_request_floor` is not an absence of data,
    // it is the data saying a percentile would be meaningless over 12 samples.
    case 'ok':
    case 'empty':
    case 'below_request_floor':
    case 'above_top_bucket':
      return 'measured';
    // Terminal: phase 0 proved this can never return data today.
    case 'blocked':
      return 'unmeasurable';
    // `unproven`, `error`, and anything this build does not recognise.
    default:
      return 'unmeasured';
  }
}

/** True only for states that carry a real reading. Used to gate sparklines,
 *  bars and any other purely-graphical encoding — a bar drawn for an
 *  `unproven` is a bar drawn from nothing. */
export function isMeasured(status: string): boolean {
  return epistemicFor(status) === 'measured';
}

/**
 * The one-sentence gloss shown in the reading key and in every chip's tooltip.
 *
 * Written for the operator, not the schema: it says what the state means about
 * the WORLD, which is the thing a verdict has to be read against.
 */
export function describeStatus(status: string): string {
  switch (status) {
    case 'ok':
      return 'measured, and this is the reading';
    case 'empty':
      return 'measured: the query was gate-proven and legitimately matched nothing. Not zero.';
    case 'unproven':
      return 'NOT measured: the widened sibling query proved nothing, so emptiness here says nothing about the world. This can never support a green verdict.';
    case 'blocked':
      return 'CANNOT be measured today: there is no scrape or no exporter. Terminal, never queried.';
    case 'error':
      return 'NOT measured: the collector failed, so we do not know what the world looks like.';
    case 'above_top_bucket':
      return 'measured: the real value is off the top of the histogram, so no finite percentile exists.';
    case 'below_request_floor':
      return 'measured: too few requests for a percentile to mean anything.';
    default:
      return 'an unrecognised status — treated as not measured';
  }
}

/**
 * A short glyph that pairs with the word.
 *
 * Never used alone — `labelFor` always accompanies it. It exists so the state is
 * legible at a glance without depending on hue, which is the failure mode for
 * the ~8 % of men with a red/green deficiency and for anyone outdoors.
 *
 * On screen this is superseded by {@link iconFor}; it survives for the places
 * that need a plain string (tooltips, the `title` attribute, tests).
 */
export function glyphFor(status: string): string {
  // Two statuses earn their own mark because the tone they share with others
  // does not describe them: `above_top_bucket` is bad, but it is bad in the
  // specific direction of "off the top of the scale", and an arrow says that
  // where a ✕ does not.
  if (status === 'above_top_bucket') return '▲';
  if (status === 'below_request_floor') return '▽';
  switch (toneFor(status)) {
    case 'good':
      return '✓';
    case 'bad':
      return '✕';
    case 'warn':
      return '!';
    case 'blocked':
      return '⃠';
    case 'neutral':
      return '–';
  }
}

/**
 * The icon that pairs with the word — a key of `components/icons.tsx#ICONS`.
 *
 * This lives here, next to `labelFor` and `toneFor`, because it is the THIRD
 * channel of the same vocabulary and it must be exhaustive for exactly the same
 * reason they are: an unrecognised status has to come out looking like a
 * question, not like a tick. It is a pure string map so `test/ui/**` can assert
 * on it without a DOM.
 *
 * The pairings are literal rather than decorative:
 *   ok        a tick            we looked and it is fine
 *   empty     a minus           we looked and there was nothing. Not a zero.
 *   unproven  a DASHED circle   the container of an answer, with no answer in
 *                               it — the same metaphor as the dotted chip edge
 *   blocked   a strike-through  this can never be measured today
 *   error     a cross in a ring the collector failed
 *   above/below  double chevrons  off the top / under the floor of the scale
 */
export type StatusIcon =
  | 'ok'
  | 'empty'
  | 'unproven'
  | 'blocked'
  | 'error'
  | 'warn'
  | 'aboveTopBucket'
  | 'belowRequestFloor';

export function iconFor(status: string): StatusIcon {
  switch (status) {
    case 'ok':
    case 'GREEN':
    case 'healthy':
      return 'ok';
    case 'empty':
      return 'empty';
    case 'blocked':
      return 'blocked';
    case 'above_top_bucket':
      return 'aboveTopBucket';
    case 'below_request_floor':
      return 'belowRequestFloor';
    case 'error':
    case 'RED':
    case 'breached':
    case 'degraded':
      return 'error';
    case 'AMBER':
      return 'warn';
    default:
      // `unproven`, `unknown`, `unknown-role`, and anything this build has
      // never heard of: the dashed ring, which says "no answer here".
      return 'unproven';
  }
}

/**
 * Does this status need a HUE, or is ink enough?
 *
 * ---------------------------------------------------------------------------
 * THE SCARCITY RULE
 * ---------------------------------------------------------------------------
 * Colour is spent only on states that ask the operator for a decision. A
 * failure, a bad verdict, a breach: those get red or amber, and because they
 * are the only coloured things on the page, one of them appearing IS the
 * alarm.
 *
 * Everything that is merely UNKNOWN is drawn in ink. `unproven` and `blocked`
 * are honest absences, not incidents — an unjudged sweep is the normal
 * operation of a delta-triggered agent, and ProxySQL having had no exporter
 * since #356 is a structural fact that has been true for months. Painting
 * either of them amber, as the previous round did, spent the alarm colour on
 * the steady state and left nothing louder for the actual alarm.
 *
 * This does NOT weaken the four-way vocabulary: `unproven` is still separated
 * from `empty` and from `ok` by its WORD, its ICON and its DOTTED container.
 * Three channels, none of which is hue. `toneFor` is untouched and remains the
 * semantic answer to "is it bad"; this is the rendering answer to "does it get
 * to shout".
 */
export function isAlarm(status: string): boolean {
  switch (status) {
    case 'error':
    case 'RED':
    case 'AMBER':
    case 'breached':
    case 'degraded':
    case 'above_top_bucket':
      return true;
    default:
      return false;
  }
}

/**
 * Why a signal is not `ok`, in a sentence.
 *
 * Returns `null` for an `ok` signal. Everything else gets the most specific text
 * the backend supplied, falling back to the reason code, and never to ''.
 */
export function explain(signal: Signal<unknown>): string | null {
  if (signal.status === 'ok') return null;
  if (typeof signal.detail === 'string' && signal.detail !== '') return signal.detail;
  if (typeof signal.message === 'string' && signal.message !== '') {
    return signal.kind === undefined ? signal.message : `${String(signal.kind)}: ${signal.message}`;
  }
  if (typeof signal.reason === 'string' && signal.reason !== '') return signal.reason;
  if (typeof signal.gate === 'string' && signal.gate !== '') return `gate ${signal.gate}`;
  return labelFor(signal.status);
}

export interface ValueTextOptions {
  /** Applied to an `ok` value only. */
  format?: (value: unknown) => string;
  unit?: string;
}

/**
 * The text a cell shows for a signal.
 *
 * For `ok`, the formatted value (plus unit). For everything else, the WORD —
 * never a zero, never a blank, never an em-dash that could be read as "fine".
 */
export function valueText(signal: Signal<unknown>, options: ValueTextOptions = {}): string {
  if (signal.status !== 'ok') return labelFor(signal.status);
  const raw = signal.value;
  const formatted = options.format !== undefined ? options.format(raw) : defaultFormat(raw);
  return options.unit === undefined ? formatted : `${formatted} ${options.unit}`;
}

function defaultFormat(value: unknown): string {
  if (typeof value === 'number') return formatNumber(value);
  if (typeof value === 'string') return value;
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  if (value === null || value === undefined) return 'no value';
  return JSON.stringify(value);
}

/** Compact, locale-free number formatting. Deterministic across machines. */
export function formatNumber(value: number, maxFractionDigits = 2): string {
  if (!Number.isFinite(value)) return String(value);
  if (Number.isInteger(value)) return value.toLocaleString('en-GB');
  return value.toLocaleString('en-GB', { maximumFractionDigits: maxFractionDigits });
}

/* ------------------------------------------------------------------ *
 * Percentiles
 * ------------------------------------------------------------------ */

export interface PercentileSignalView {
  status: string;
  quantile?: number;
  seconds?: number;
  requests?: number;
  minRequests?: number;
  topBucketLe?: string;
  withinTopBucket?: number;
  reason?: string;
  detail?: string;
  gate?: string;
  kind?: string;
  message?: string;
}

/**
 * The latency cell.
 *
 * `below_request_floor` and `above_top_bucket` are printed AS WORDS, with the
 * numbers that make them actionable. A p95 of "below request floor (12 of 100
 * requests)" is a complete, honest answer; "0 ms" and "—" are both lies.
 */
export function percentileText(p: PercentileSignalView): string {
  switch (p.status) {
    case 'ok':
      return p.seconds === undefined ? 'ok' : formatSeconds(p.seconds);
    case 'above_top_bucket':
      return `above top bucket (> ${p.topBucketLe ?? '10'} s)`;
    case 'below_request_floor':
      return `below request floor (${formatNumber(p.requests ?? 0)} of ${formatNumber(
        p.minRequests ?? 0,
      )})`;
    default:
      return labelFor(p.status);
  }
}

export function formatSeconds(seconds: number): string {
  if (!Number.isFinite(seconds)) return String(seconds);
  if (seconds < 1) return `${formatNumber(seconds * 1000, 0)} ms`;
  return `${formatNumber(seconds, 2)} s`;
}
